// BulkAddCardholders.cshtml: upload an .xlsx (Email, First Name, Last Name), review every row, then confirm. The rows
// stay on the server between the two steps (the BFF's import batch), so what is confirmed is what was previewed.
// A program whose cards staff issue takes its own sheet (amount and address on every row): confirming issues and loads a card per
// row in the background, and the page follows it until every row has its outcome.
import { api, BffError, download, GENERIC_ERROR } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { useStaff } from '../components/RequireStaff';
import { ChooseProcessor, FormError, PageHeading } from '../components/ui';
import { assignablePrograms } from '../scope';

export interface ImportRow {
  rowNumber: number;
  email: string;
  firstName: string;
  lastName: string | null;
  isValid: boolean;
  error: string | null;
  /** Card sheets only (instant issue); null: the card is issued without a load. */
  amount?: string | null;
  city?: string;
  region?: string;
}
interface Preview {
  batchId: string;
  kind?: 'cardholders' | 'cards';
  programName: string;
  rows: ImportRow[];
}
export interface IssueResult {
  rowNumber: number;
  email: string;
  success: boolean;
  cardholderId: string | null;
  last4: string | null;
  amount: string | null;
  loadStatus: string | null;
  error: string | null;
}
export interface IssueRun {
  batchId: string;
  status: string;
  programName: string;
  total: number;
  done: number;
  results: IssueResult[];
}

/** What a card row came to, in the console's words. */
export function issueOutcome(r: IssueResult): { label: string; tone: 'success' | 'warning' } {
  if (!r.success) return { label: r.last4 ? 'Issued, not loaded' : 'Failed', tone: 'warning' };
  return r.loadStatus === 'pending_approval'
    ? { label: 'Issued, load awaits approval', tone: 'success' }
    : { label: 'Issued', tone: 'success' };
}
interface ImportResult {
  rowNumber: number;
  email: string;
  success: boolean;
  error: string | null;
}

const plural = (n: number) => (n === 1 ? '' : 's');
const errorText = (err: unknown) => (err instanceof BffError ? err.message : GENERIC_ERROR);

export function BulkAddCardholdersPage() {
  usePageTitle('Bulk Add Cardholders');
  const { scope } = useStaff();
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs, enabled: !scope.needsProvider });
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [results, setResults] = useState<{ programName: string; results: ImportResult[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  // a card import runs in the background: followed until every row has its outcome
  const run = useQuery({
    queryKey: ['card-import', runId],
    enabled: runId !== null,
    queryFn: () => api<IssueRun>('GET', `/admin/cardholder-imports/${encodeURIComponent(runId!)}`),
    refetchInterval: (q) => (q.state.data?.status === 'completed' ? false : 1500),
  });
  const instantProgram = (programs.data ?? []).find((p) => p.id === chosen)?.instantIssue === true;

  async function upload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const programId = String(form.get('SelectedProgramId') ?? '');
    const file = (e.currentTarget.elements.namedItem('UploadedFile') as HTMLInputElement | null)?.files?.[0] ?? null;
    setBusy(true);
    try {
      setPreview(
        await api<Preview>('POST', `/admin/cardholder-imports?programId=${encodeURIComponent(programId)}`, {
          body: new Blob(file instanceof File ? [await file.arrayBuffer()] : [], { type: 'application/octet-stream' }),
          headers: { 'X-File-Name': file instanceof File ? file.name : '' },
        }),
      );
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(batchId: string, kind: Preview['kind']) {
    setBusy(true);
    try {
      const answer = await api<{ programName: string; results: ImportResult[] }>(
        'POST',
        `/admin/cardholder-imports/${encodeURIComponent(batchId)}/confirm`,
      );
      if (kind === 'cards') {
        setPreview(null);
        setRunId(batchId);
        return;
      }
      setResults(answer);
    } catch (err) {
      setPreview(null);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setPreview(null);
    setResults(null);
    setRunId(null);
    setError(null);
  }

  let body;
  if (scope.needsProvider) body = <ChooseProcessor what="before importing cardholders" />;
  else if (runId) {
    const data = run.data;
    const finished = data?.status === 'completed';
    const issued = (data?.results ?? []).filter((r) => r.success).length;
    const failed = (data?.results ?? []).length - issued;
    body = (
      <>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <div className="qc-panel-title">
            <h3>{finished ? 'Cards issued' : 'Issuing cards…'}</h3>
            <span>{data?.programName}</span>
          </div>
          <p style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
            {data ? `${data.done} of ${data.total} done` : 'Starting…'}
            {issued > 0 && (
              <>
                {' '}
                &middot; <span style={{ color: 'var(--qor-success)' }}>{issued} issued</span>
              </>
            )}
            {failed > 0 && (
              <>
                {' '}
                &middot; <span style={{ color: 'var(--qor-red-dark)' }}>{failed} need attention</span>
              </>
            )}
          </p>
          {!finished && (
            <p className="qc-empty" style={{ margin: 0 }}>
              Each card is issued and loaded at the processor, a few seconds a row. You can leave this page: the import
              goes on, and reopening it is safe — no card is issued or loaded twice.
            </p>
          )}
        </div>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Email</th>
                <th>Card</th>
                <th>Amount</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {(data?.results ?? []).map((r) => {
                const outcome = issueOutcome(r);
                return (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td>{r.cardholderId ? <Link to={`/cardholders/${r.cardholderId}`}>{r.email}</Link> : r.email}</td>
                    <td>{r.last4 ? `•••• ${r.last4}` : '--'}</td>
                    <td>{r.amount === null ? 'No load' : `$${r.amount}`}</td>
                    <td>
                      <span className={`qc-badge qc-badge-${outcome.tone}`} title={r.error ?? undefined}>
                        {outcome.label}
                      </span>
                      {r.error && (
                        <div style={{ color: 'var(--qor-muted)', fontSize: '.75rem', marginTop: 4 }}>{r.error}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {finished && (
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to="/cardholders" className="qor-button qor-button-primary">
              Done
            </Link>
            <a
              href="/cardholders/import"
              className="qc-button-ghost"
              onClick={(e) => {
                e.preventDefault();
                startOver();
              }}
            >
              Import another file
            </a>
          </div>
        )}
      </>
    );
  } else if (results) {
    const succeeded = results.results.filter((r) => r.success).length;
    const failed = results.results.length - succeeded;
    body = (
      <>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <div className="qc-panel-title">
            <h3>Import complete</h3>
            <span>{results.programName}</span>
          </div>
          <p style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
            <span style={{ color: 'var(--qor-success)' }}>{succeeded} created</span>
            {failed > 0 && (
              <>
                {' '}
                &middot; <span style={{ color: 'var(--qor-red-dark)' }}>{failed} failed</span>
              </>
            )}
          </p>
        </div>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Email</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {results.results.map((r) => (
                <tr key={r.rowNumber}>
                  <td>{r.rowNumber}</td>
                  <td>{r.email}</td>
                  <td>
                    {r.success ? (
                      <span className="qc-badge qc-badge-success">Created</span>
                    ) : (
                      <span className="qc-badge qc-badge-warning" title={r.error ?? undefined}>
                        Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/cardholders" className="qor-button qor-button-primary">
            Done
          </Link>
          <a
            href="/cardholders/import"
            className="qc-button-ghost"
            onClick={(e) => {
              e.preventDefault();
              startOver();
            }}
          >
            Import another file
          </a>
        </div>
      </>
    );
  } else if (preview) {
    const issuing = preview.kind === 'cards';
    const ready = preview.rows.filter((r) => r.isValid).length;
    const skipped = preview.rows.length - ready;
    body = (
      <>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <div className="qc-panel-title">
            <h3>Confirm before you import</h3>
            <span>{preview.programName}</span>
          </div>
          <p style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
            <span style={{ color: 'var(--qor-success)' }}>{ready} ready to create</span>
            {skipped > 0 && (
              <>
                {' '}
                &middot; <span style={{ color: 'var(--qor-red-dark)' }}>{skipped} will be skipped</span>
              </>
            )}
          </p>
          <p className="qc-empty" style={{ margin: 0 }}>
            {issuing
              ? `Nothing has been issued yet. Review every row below, then confirm to issue and load ${ready} card${plural(ready)} in ${preview.programName}.`
              : `Nothing has been created yet. Review every row below, then confirm to add the ${ready} ready row${plural(ready)} to ${preview.programName}.`}
          </p>
        </div>
        <div className="qc-panel" style={{ marginBottom: 20 }}>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Email</th>
                <th>First name</th>
                <th>Last name</th>
                {issuing && <th>Amount</th>}
                {issuing && <th>City</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.rowNumber}>
                  <td>{r.rowNumber}</td>
                  <td>{r.email}</td>
                  <td>{r.firstName}</td>
                  <td>{r.lastName ?? '--'}</td>
                  {issuing && <td>{r.amount ? `$${r.amount}` : 'No load'}</td>}
                  {issuing && <td>{[r.city, r.region].filter(Boolean).join(', ') || '--'}</td>}
                  <td>
                    {r.isValid ? (
                      <span className="qc-badge qc-badge-success">Ready</span>
                    ) : (
                      <span className="qc-badge qc-badge-warning" title={r.error ?? undefined}>
                        {r.error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="qor-button qor-button-primary"
            disabled={ready === 0 || busy}
            onClick={() => void confirm(preview.batchId, preview.kind)}
          >
            {issuing
              ? `Confirm & Issue ${ready} Card${plural(ready)}`
              : `Confirm & Add ${ready} Cardholder${plural(ready)}`}
          </button>
          <a
            href="/cardholders/import"
            className="qc-button-ghost"
            onClick={(e) => {
              e.preventDefault();
              startOver();
            }}
          >
            Upload a different file
          </a>
        </div>
      </>
    );
  } else
    body = (
      <div className="qc-panel" style={{ maxWidth: 640 }}>
        <div className="qc-panel-title">
          <h3>Upload a spreadsheet</h3>
          <span>Adds every valid row to one program you choose below</span>
        </div>
        <FormError message={error} />
        <form onSubmit={(e) => void upload(e)}>
          <label className="qc-account-field">
            <span>Program</span>
            <select name="SelectedProgramId" value={chosen} onChange={(e) => setChosen(e.target.value)}>
              <option value="">Choose a program...</option>
              {assignablePrograms(programs.data ?? [], scope).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="qc-account-field">
            <span>Spreadsheet (.xlsx)</span>
            {/* no file: the BFF says "Choose a .xlsx file to upload." */}
            <input type="file" name="UploadedFile" accept=".xlsx" />
          </label>
          <p className="qc-empty" style={{ margin: '0 0 18px' }}>
            {instantProgram
              ? 'One card per row, issued and loaded when you confirm. Columns, in order: Email, First Name, Last Name, Amount (empty or 0: the card is issued without a load), Street Address, City, State (NY or New York), ZIP, Country (optional; US addresses only), Phone (optional). First row is treated as a header.'
              : 'Columns, in order: Email, First Name, Last Name (optional). First row is treated as a header.'}{' '}
            <a
              href="/cardholders/import"
              onClick={(e) => {
                e.preventDefault();
                void (instantProgram
                  ? download('/admin/cardholder-imports/template?kind=cards', 'qor-card-import-template.xlsx')
                  : download('/admin/cardholder-imports/template', 'qor-cardholder-import-template.xlsx'));
              }}
            >
              Download a template
            </a>
            .
          </p>
          <button type="submit" className="qc-account-refresh" disabled={busy}>
            Preview Import
          </button>
        </form>
      </div>
    );

  return (
    <>
      <PageHeading title="Bulk Add Cardholders" trail={[['Cardholders', '/cardholders']]} />
      {body}
    </>
  );
}
